/**
 * HaDocumentSubList: collapsed "N HA documents" toggle rendered under a drug
 * row when the drug result carries ``ha_documents`` (corpus="ha_reviews").
 *
 * Links reuse the FDA review viewer route (same URL shape as the retired
 * SbaDocumentResults helpers, Plan D7):
 * ``/drug/{id}?source=sba&version={physical document id}&sbaChunkIds=...``.
 * Every link is a real anchor so middle-click / cmd-click work.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HADocumentEvidence, HADocumentResult } from '../types';

type Association = NonNullable<HADocumentResult['submission_associations']>[number];

/** Route for a document row: the drug viewer, deep-linked to chunks of the primary physical document. */
export function haDocumentHref(drugId: number | null | undefined, doc: HADocumentResult): string | null {
  if (drugId == null) return null;
  const params = new URLSearchParams();
  params.set('source', 'sba');
  params.set('version', String(doc.document_id));
  const chunkIds = doc.evidence
    .filter((e) => (e.document_id ?? doc.document_id) === doc.document_id)
    .map((e) => e.chunk_id)
    .filter((id): id is number => typeof id === 'number');
  if (chunkIds.length > 0) params.set('sbaChunkIds', chunkIds.join(','));
  return `/drug/${drugId}?${params.toString()}`;
}

/** Route for one evidence row: its own physical document and exact chunk. */
export function haEvidenceHref(drugId: number | null | undefined, ev: HADocumentEvidence): string | null {
  if (drugId == null) return null;
  const params = new URLSearchParams();
  params.set('source', 'sba');
  params.set('version', String(ev.document_id));
  params.set('sbaChunkIds', String(ev.chunk_id));
  return `/drug/${drugId}?${params.toString()}`;
}

const typeLabel = (doc: HADocumentResult): string => {
  if (doc.effective_doc_type_labels?.length) return doc.effective_doc_type_labels.join(' + ');
  return doc.doc_type_label || 'FDA review';
};

const submissionLabel = (type: string | null | undefined, number: number | null | undefined): string => {
  const t = type?.toUpperCase();
  if (t === 'ORIG') return 'Original submission';
  if (t === 'SUPPL') return number == null ? 'Supplement' : `Supplement ${number}`;
  if (type) return number == null ? type : `${type} ${number}`;
  return 'FDA review';
};

const submissionLabels = (doc: HADocumentResult): string[] => {
  const assoc: Association[] | undefined = doc.submission_associations;
  if (Array.isArray(assoc) && assoc.length > 0) {
    return assoc.map((a) => submissionLabel(a.submission_type, a.submission_number));
  }
  if (Array.isArray(assoc)) return ['Unclassified review'];
  if (doc.fda_submission_id != null) return [submissionLabel(doc.submission_type, doc.submission_number)];
  return ['FDA review'];
};

interface Props {
  drugId: number;
  documents: HADocumentResult[];
}

const HaDocumentSubList: React.FC<Props> = ({ drugId, documents }) => {
  const [open, setOpen] = useState(false);
  if (!documents.length) return null;
  return (
    <div className="pl-6" data-testid="ha-document-sublist">
      <button
        type="button"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {documents.length} HA {documents.length === 1 ? 'document' : 'documents'}
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {documents.map((doc) => {
            const href = haDocumentHref(drugId, doc);
            const name = doc.source_document_name || typeLabel(doc);
            return (
              <li
                key={doc.logical_document_key}
                data-testid="ha-document-row"
                className="relative rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-indigo-200"
              >
                <div className="text-sm font-medium text-slate-800 truncate">
                  {href ? (
                    // Stretched link: the whole row is one real anchor target.
                    <Link
                      to={href}
                      className="rounded after:absolute after:inset-0 after:rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {name}
                    </Link>
                  ) : (
                    name
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                  <span className="font-medium text-indigo-600">{typeLabel(doc)}</span>
                  {submissionLabels(doc).map((label, i) => (
                    <span key={`${label}-${i}`} className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                      {label}
                    </span>
                  ))}
                </div>
                {doc.evidence.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {doc.evidence.map((ev, i) => {
                      const evHref = haEvidenceHref(drugId, ev);
                      const body = (
                        <>
                          <span className="w-12 flex-shrink-0 text-xs font-medium text-slate-400">
                            {ev.section_number || '?'}
                          </span>
                          <span className="min-w-0 flex-1 text-slate-600">
                            {ev.section_title && (
                              <span className="mr-1.5 text-xs font-medium text-indigo-500">{ev.section_title}</span>
                            )}
                            {ev.snippet}
                          </span>
                        </>
                      );
                      const key = `${ev.document_id}:${ev.chunk_id}:${i}`;
                      // ``relative z-10`` lifts the row above the stretched card link
                      // so it keeps its OWN href (physical document + chunk).
                      return evHref ? (
                        <Link
                          key={key}
                          to={evHref}
                          aria-label={`Open evidence ${ev.section_number || ev.chunk_id}`}
                          className="relative z-10 flex w-full gap-2 rounded p-1 -m-1 text-left text-sm hover:bg-indigo-50"
                        >
                          {body}
                        </Link>
                      ) : (
                        <div key={key} className="relative z-10 flex w-full gap-2 p-1 -m-1 text-left text-sm">
                          {body}
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default HaDocumentSubList;
